import { useEffect, useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { createMessageSocket } from '@/lib/message-socket';
import { useMessageStore } from '@/stores/message-store';
import { useAuthStore } from '@/stores/auth-store';
import type { Socket } from 'socket.io-client';

export function useMessageSocket() {
  const socketRef = useRef<Socket | null>(null);
  const queryClient = useQueryClient();
  const { enqueuePopup, setUnreadCount } = useMessageStore();
  const accessToken = useAuthStore((s) => s.accessToken);

  useEffect(() => {
    if (!accessToken) return;

    const socket = createMessageSocket({ token: accessToken });
    socketRef.current = socket;

    socket.on('connect', () => {
      queryClient.invalidateQueries({ queryKey: ['messages', 'unread-count'] });
    });

    socket.on('message:new', (event: any) => {
      enqueuePopup(event);
      queryClient.invalidateQueries({ queryKey: ['messages', 'inbox'] });
      queryClient.invalidateQueries({ queryKey: ['messages', 'unread-count'] });
    });

    socket.on('message:reply', () => {
      queryClient.invalidateQueries({ queryKey: ['messages', 'inbox'] });
    });

    socket.on('unread:count', (data: { count: number }) => {
      setUnreadCount(data.count);
    });

    socket.on('error', (err: any) => {
      console.error('Message socket error:', err);
    });

    socket.connect();

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [accessToken]);

  const markRead = useCallback(
    (conversationId: string) => {
      socketRef.current?.emit('markRead', { conversationId });
    },
    [],
  );

  const sendTyping = useCallback(
    (conversationId: string) => {
      socketRef.current?.emit('typing', { conversationId });
    },
    [],
  );

  return { markRead, sendTyping };
}
